<template>
    <v-card
        :to="to"
        :href="href"
        :target="target"
        :disabled="disabled"
        :ripple="false"
        height="100%"
        class="d-flex flex-column"
        :class="{ 'clickable-card': is_clickable }"
        @click="handle_click"
    >
        <div class="flex-shrink-0 position-relative">
            <slot name="image_overlay" />

            <v-img
                v-if="image_url && !image_error"
                :src="image_url"
                width="100%"
                :height="image_height"
                :alt="image_alt"
                :cover="image_fit === 'cover'"
                :contain="image_fit === 'contain'"
                :position="image_position"
                class="card-image flex-shrink-0"
                @error="image_error = true"
            />

            <div
                v-else
                class="d-flex align-center justify-center flex-shrink-0"
                :style="`height: ${image_height}px;`"
            >
                <v-icon
                    size="160"
                    color="grey lighten-2"
                    aria-label="Image not available"
                >
                    {{ fallback_icon }}
                </v-icon>
            </div>
        </div>

        <div class="d-flex flex-column flex-grow-1">
            <slot name="content" />

            <div class="mt-auto">
                <slot name="actions" />
            </div>
        </div>
    </v-card>
</template>

<script>
export default {
    data() {
        return {
            image_error: false,
        };
    },

    props: {
        image_url: {
            type: String,
            default: null,
        },

        image_fit: {
            type: String,
            default: 'cover',
        },

        image_position: {
            type: String,
            default: 'center center',
        },

        image_height: {
            type: [Number, String],
            default: 340,
        },

        image_alt: {
            type: String,
            default: '',
        },

        fallback_icon: {
            type: String,
            default: 'mdi-image-off',
        },

        to: {
            type: [String, Object],
            default: null,
        },

        href: {
            type: String,
            default: null,
        },

        target: {
            type: String,
            default: null,
        },

        disabled: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        is_clickable() {
            return this.to || this.href || this.$listeners.click;
        },
    },

    methods: {
        handle_click(event) {
            if (this.is_interactive_element(event.target)) {
                return;
            }

            if (this.$listeners.click) {
                this.$emit('click', event);
            }
        },

        // need to avoid triggering click when clicking on interactive elements inside a clickable card
        // .self event modifier doesn't work because we only want to ignore clicks on interactive elements, not the whole card
        is_interactive_element(element) {
            const interactive_selectors = [
                'button',
                'a',
                'input',
                'select',
                'textarea',
                '[role="button"]',
                '.v-btn',
                '.v-switch',
                '.v-checkbox',
                '.v-radio',
                '.v-slider',
                '.v-menu',
                '.v-tooltip',
            ];

            let current_element = element;
            while (current_element && current_element !== this.$el) {
                for (const selector of interactive_selectors) {
                    if (current_element.matches && current_element.matches(selector)) {
                        return true;
                    }
                }

                // also check for elements with click handlers
                if (current_element.onclick || current_element.addEventListener) {
                    const has_click_listener = current_element.getAttribute('data-has-click') ||
                        current_element.classList.contains('clickable') ||
                        current_element.style.cursor === 'pointer';
                    if (has_click_listener) {
                        return true;
                    }
                }

                current_element = current_element.parentElement;
            }

            return false;
        },
    },

    watch: {
        image_url() {
            this.image_error = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.clickable-card {
    cursor: pointer;

    &:hover {
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15) !important;
        transform: translateY(-1px);
        transition: all 0.2s ease;
    }

    &.v-card--disabled {
        cursor: not-allowed;
        opacity: 0.6;

        &:hover {
            box-shadow: none !important;
            transform: none;
        }
    }
}

.card-image {
    background-color: var(--color-bg-shaded-light);
    border-top-left-radius: var(--border-radius-default);
    border-top-right-radius: var(--border-radius-default);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}
</style>